// /src/ui/layout/Header.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { loginStayOnPage } from "../../core/login";
import { logoutToIndex } from "../../core/logout";

type AdminMenuItem = {
  label: string;
  href: string;
};

type HeaderProps = {
  brandName: string;
  tagline: string;
  logoSrc: string;

  authStatus?: "loading" | "authed" | "anon";
  authed: boolean;
  fullWidth?: boolean;

  // display in the dropdown trigger (displayName preferred; email fallback)
  accountLabel?: string;

  homeHref?: string;
  portalHref?: string;
  profileHref?: string;
  
  // admin menu items (optional)
  adminMenuItems?: AdminMenuItem[];
};

function normalizePath(href: string): string {
  try {
    return new URL(href, window.location.origin).pathname;
  } catch {
    return href;
  }
}

function isCurrentPage(href: string): boolean {
  const cur = window.location.pathname;
  const target = normalizePath(href);
  return cur === target;
}

function HamburgerIcon(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={props.className ?? "h-5 w-5"} aria-hidden="true">
      <path
        fill="currentColor"
        d="M4 6.5h16v1.5H4V6.5zm0 5h16V13H4v-1.5zm0 5h16V18H4v-1.5z"
      />
    </svg>
  );
}

export function Header({
  brandName,
  tagline,
  logoSrc,
  authStatus,
  authed,
  fullWidth,
  accountLabel,
  homeHref = "/index.html",
  portalHref = "/pages/portal/index.html",
  profileHref = "/pages/profile/index.html",
  adminMenuItems,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const desktopMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;

      const desktopEl = desktopMenuRef.current;
      const mobileEl = mobileMenuRef.current;

      const target = e.target;
      if (!(target instanceof Node)) return;

      const inDesktop = desktopEl ? desktopEl.contains(target) : false;
      const inMobile = mobileEl ? mobileEl.contains(target) : false;

      if (!inDesktop && !inMobile) setMenuOpen(false);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (!menuOpen) return;
      if (e.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("click", onDocClick); // bubble
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);


  const linkClass = "text-sm text-muted hover:text-fg hover:underline underline-offset-4";
  const disabledLinkClass = "text-sm text-muted/60 cursor-default";

  const label = accountLabel?.trim() ? accountLabel : "Account";

  const onProfileAction = useMemo(() => {
    const onProfile = isCurrentPage(profileHref);
    if (onProfile) {
      return {
        kind: "refresh" as const,
        text: "Refresh",
        onClick: () => window.location.reload(),
      };
    }
    return {
      kind: "link" as const,
      text: "Edit",
      href: profileHref,
    };
  }, [profileHref]);

  const portalIsCurrent = useMemo(() => isCurrentPage(portalHref), [portalHref]);

  // Public CTAs: keep minimal in header; your hero already has strong CTA
  // const publicPrimaryHref = profileHref;
  // const publicPrimaryText = "Create Profile";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-bg/95 backdrop-blur">
      <div className={`${fullWidth ? 'w-full' : 'mx-auto max-w-6xl'} flex items-center justify-between px-4 py-3`}>
        <a href={homeHref} className="flex items-center gap-2">
          <img
            src={logoSrc}
            alt={`${brandName} logo`}
            width={80}
            height={32}
            className="h-8 w-20"
          />
          <div className="leading-tight">
            <div className="text-sm font-semibold">{brandName}</div>
            <div className="text-[11px] text-muted">{tagline}</div>
          </div>
        </a>

        {/* Right side */}
        <nav className="flex items-center gap-3">
          {authStatus === "loading" ? null : (
            // Desktop links
            <div className="hidden sm:flex items-center gap-4">
              {authed ? (
                <>

                  {/* Admin menu items */}
                  {adminMenuItems && adminMenuItems.length > 0 && (
                    <>
                      {adminMenuItems.map((item) => {
                        const isCurrent = isCurrentPage(item.href);
                        return isCurrent ? (
                          <span
                            key={item.label}
                            className={disabledLinkClass}
                            aria-current="page"
                          >
                            {item.label}
                          </span>
                        ) : (
                          <a
                            key={item.label}
                            className={linkClass}
                            href={item.href}
                          >
                            {item.label}
                          </a>
                        );
                      })}
                      <span className="text-muted">·</span>
                    </>
                  )}

                  
                  {portalIsCurrent ? (
                    <span className={disabledLinkClass} aria-current="page">
                      Portal
                    </span>
                  ) : (
                    <a className={linkClass} href={portalHref}>
                      Portal
                    </a>
                  )}

                  {/* User dropdown (desktop) */}
                  <div className="relative" ref={desktopMenuRef}>
                    <button
                      type="button"
                      className={linkClass}
                      aria-haspopup="menu"
                      aria-expanded={menuOpen ? "true" : "false"}
                      onClick={() => setMenuOpen((v) => !v)}
                    >
                      <span className="max-w-[220px] truncate">{label}</span>
                    </button>

                    {menuOpen ? (
                      <div role="menu" className="ui-menu absolute right-0 mt-2 w-44">
                        {onProfileAction.kind === "link" ? (
                          <a
                            role="menuitem"
                            className="ui-menu-item"
                            href={onProfileAction.href}
                            onClick={() => setMenuOpen(false)}
                          >
                            {onProfileAction.text}
                          </a>
                        ) : (
                          <button
                            role="menuitem"
                            type="button"
                            className="ui-menu-item"
                            onClick={() => {
                              setMenuOpen(false);
                              onProfileAction.onClick();
                            }}
                          >
                            {onProfileAction.text}
                          </button>
                        )}

                        <button
                          role="menuitem"
                          type="button"
                          className="ui-menu-item"
                          onClick={() => {
                            setMenuOpen(false);
                            logoutToIndex();
                          }}
                        >
                          Logout
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {/* <a className={linkClass} href={publicPrimaryHref}>
                    {publicPrimaryText}
                  </a> */}
                  {portalIsCurrent ? ( /* should never happen if not authed */
                    <span className={disabledLinkClass} aria-current="page">
                      Portal
                    </span>
                  ) : (
                    <a className={linkClass} href={portalHref}>
                      Activate Portal
                    </a>
                  )}
                  <button type="button" className={linkClass} onClick={loginStayOnPage}>
                    Login
                  </button>
                </>
              )}
            </div>
          )}

          {/* Mobile hamburger */}
          <div className="relative sm:hidden" ref={mobileMenuRef}>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-border bg-bg px-3 py-2 text-sm text-fg shadow-soft hover:bg-card"
              aria-haspopup="menu"
              aria-expanded={menuOpen ? "true" : "false"}
              onClick={() => setMenuOpen((v) => !v)}
              title="Menu"
            >
              <HamburgerIcon className="h-5 w-5" />
            </button>

            {menuOpen ? (
              <div role="menu" className="ui-menu absolute right-0 mt-2 w-56">
                {authed ? (
                  <>
                    <div className="px-3 py-2 text-xs text-muted">
                      Signed in as <span className="font-semibold text-fg">{label}</span>
                    </div>

                    <div className="border-t border-border my-1" />

                    {/* Admin menu items (mobile) */}
                    {adminMenuItems && adminMenuItems.length > 0 && (
                      <>
                        {adminMenuItems.map((item) => {
                          const isCurrent = isCurrentPage(item.href);
                          return isCurrent ? (
                            <div
                              key={item.label}
                              className={`ui-menu-item ${disabledLinkClass}`}
                              aria-current="page"
                            >
                              {item.label}
                            </div>
                          ) : (
                            <a
                              key={item.label}
                              role="menuitem"
                              className="ui-menu-item"
                              href={item.href}
                              onClick={() => setMenuOpen(false)}
                            >
                              {item.label}
                            </a>
                          );
                        })}
                        <div className="border-t border-border my-1" />
                      </>
                    )}


                    {portalIsCurrent ? (
                      <div className="ui-menu-item opacity-60 cursor-default" aria-current="page">
                        Portal
                      </div>
                    ) : (
                      <a
                        role="menuitem"
                        className="ui-menu-item"
                        href={portalHref}
                        onClick={() => setMenuOpen(false)}
                      >
                        Portal
                      </a>
                    )}

                    {onProfileAction.kind === "link" ? (
                      <a
                        role="menuitem"
                        className="ui-menu-item"
                        href={onProfileAction.href}
                        onClick={() => setMenuOpen(false)}
                      >
                        {onProfileAction.text}
                      </a>
                    ) : (
                      <button
                        role="menuitem"
                        type="button"
                        className="ui-menu-item"
                        onClick={() => {
                          setMenuOpen(false);
                          onProfileAction.onClick();
                        }}
                      >
                        {onProfileAction.text}
                      </button>
                    )}

                    <button
                      role="menuitem"
                      type="button"
                      className="ui-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        logoutToIndex();
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      role="menuitem"
                      className="ui-menu-item"
                      href={portalHref}
                      onClick={() => setMenuOpen(false)}
                    >
                      Activate Portal
                    </a>

                    <button
                      role="menuitem"
                      type="button"
                      className="ui-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        loginStayOnPage();
                      }}
                    >
                      Login
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </nav>
      </div>
    </header>
  );
}
